import { Dialog } from '@angular/cdk/dialog';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlarmsService } from '@API/alarms/alarms.service';
import { AngularCdkModule } from '@app/modules/angular-cdk/angular-cdk.module';
import { AlarmModalComponent } from '@alarms/components/alarm-modal/alarm-modal.component';
import { Alarm } from '@interfaces/alarm';
import { ComponentStatus } from '@utils/constants';

@Component({
  selector: 'add-alert-fb',
  standalone: true,
  imports: [CommonModule, AngularCdkModule],
  templateUrl: './add-alert-fb.component.html',
  styleUrls: ['./add-alert-fb.component.scss'],
})
export class AddAlertFbComponent implements OnInit {
  private readonly _dialog = inject(Dialog);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _alarmService = inject(AlarmsService);

  public readonly pendingAlarms = this._alarmService.alarmList;
  public readonly bellStatus = signal<ComponentStatus>('loading');
  public isOpen = false;
  public isModal = false;
  public status: ComponentStatus = 'init';
  public alarmsList = signal<Alarm[]>([]);
  public page = 1;
  public pageSize = 5;
  public hasMoreData = signal<boolean>(true);

  ngOnInit(): void {
    this._loadBellNotifications();
  }

  public changeFlag(): void {
    if (!this.isModal) {
      this.isOpen = !this.isOpen;
      if (!this.isOpen) {
        this._resetData();
      }
    }
  }

  public select(item: Alarm): void {
    this.isModal = true;
    const modalRef = this._dialog.open(AlarmModalComponent, {
      data: item,
      width: '600px',
    });

    modalRef.closed.pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (markAsRead) => {
        if (markAsRead) {
          item.was_read = true;
        }
        this.isModal = false;
      },
    });
  }

  public onScroll(viewport: CdkVirtualScrollViewport): void {
    if (this.status === 'loading' || !this.hasMoreData()) {
      return;
    }

    const end = viewport.measureScrollOffset('bottom') < 50;
    if (end) {
      this.getAlarmsList();
    }
  }

  public getAlarmsList(): void {
    this.status = 'loading';

    this._alarmService
      .getNotificationList(this.page, this.pageSize, { now: true })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: ({ count, data }) => {
          this.status = 'success';
          if (count !== 0) {
            this.alarmsList.set([...this.alarmsList(), ...data]);
            this.page = this.page + 1;
            this.hasMoreData.set(this.alarmsList().length < count);
          } else {
            this.hasMoreData.set(false);
          }
        },
        error: () => {
          this.status = 'error';
        },
      });
  }

  private _loadBellNotifications(): void {
    this.bellStatus.set('loading');

    this._alarmService
      .getNotificationList(1, 5, { now: true })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this.bellStatus.set('success'),
        error: () => this.bellStatus.set('error'),
      });
  }

  private _resetData(): void {
    this.alarmsList.set([]);
    this.page = 1;
    this.hasMoreData.set(true);
  }
}
